import moment from 'moment'
import serviceInterface from '../services/service.interface'

export const getEventSource = (activity) => {
  if (!activity) return 'Unknown'
  
  const application = activity.application
  const actiontype = activity.actiontype
  const collection = activity.collection_name
  const method = activity.method
  
  // Patient registration rows (dashboard "Registered" card) are patient-centric.
  if (actiontype === 'Patient Registration') {
    return 'Patients'
  }

  // Patient-side actions (from mobile/web app)
  if (application === 'PatientApp' || actiontype === 'patietaction') {
    return 'Patients'
  }
  
  // Admin-side actions
  if (application === 'AdminWeb' || actiontype === 'useraction') {
    return 'Clinic'
  }
  
  // MoveAI device-related actions
  if (collection === 'devicemeasurement' || method?.includes('device')) {
    return 'MoveAI'
  }
  
  // Default fallback
  return 'Clinic'
}

export const getEventName = (activity) => {
  if (!activity) return 'Unknown'
  
  const method = activity.method
  const collection = activity.collection_name
  const application = activity.application
  
  // Login/Logout events
  if (method === 'login') {
    return 'Login'
  }
  if (method === 'logout') {
    return 'Logout'
  }
  // First login sets the password (dashboard "New → Logged In" card)
  if (method === 'changePassword') {
    return 'First Login'
  }
  // Patient registration (dashboard "New → Registered" card)
  if (method === 'addPatient') {
    return 'Registration'
  }
  
  // Exercise events
  if (method === 'updateExercise' && collection === 'patientexerciseschedule') {
    return 'Exercise'
  }
  if (method === 'editSchedule' && collection === 'patientexerciseschedule') {
    return 'Exercise Schedule Update'
  }
  
  // Questionnaire events
  if (method === 'updateFeedback' && collection === 'patientfeedschedule') {
    return 'Questionnaire'
  }
  if (method === 'editFeedSchedule' && collection === 'patientfeedschedule') {
    return 'Questionnaire Schedule Update'
  }
  
  // Measurement events
  if (collection === 'devicemeasurement' || collection === 'patientdeviceschedules') {
    return 'Measurement'
  }

  // TracPatch Progress Test (active ROM test)
  if (method === 'submitTracPatchData' || collection === 'tracpatchrom') {
    return 'Progress Test'
  }
  
  // Notification events
  if (method === 'notification' && collection === 'patientnotifications') {
    return 'Notification'
  }
  
  // Patient notes
  if (collection === 'patientnotes') {
    const action = activity.action
    if (action === 'add') {
      return 'Patient Note Added'
    } else if (action === 'update') {
      return 'Patient Note Updated'
    } else if (action === 'delete') {
      return 'Patient Note Deleted'
    }
    return 'Patient Note'
  }
  
  // Patient management
  if (collection === 'patients') {
    if (method === 'inactiveLog') return 'System Log'
    if (method === 'editPatient') return 'Patient Update'
    if (method === 'creatPatientAdmin') return 'Patient Creation'
    return 'Patient Activity'
  }
  
  // Case management
  if (collection === 'actioncase') {
    return 'Case Management'
  }

  // Consent form signing
  if (collection === 'patientconsents' && method === 'addConsentForRegisteredPatient') {
    return 'Consent Form Signed'
  }

  // Terms & Conditions acceptance
  if (collection === 'termsandconditions' && method === 'acceptTermsAndConditions') {
    return 'Terms & Conditions Accepted'
  }

  // Default fallback
  return method || 'Activity'
}

export const formatTimeLogged = (minutes) => {
  if (!minutes || isNaN(minutes)) return ''
  
  const hrs = Math.floor(minutes / 60)
  const mins = minutes % 60
  
  if (hrs > 0 && mins > 0) {
    return `${hrs}hr ${mins}min`
  } else if (hrs > 0) {
    return `${hrs}hr`
  } else {
    return `${mins}min`
  }
}

export const parseNewData = (newDataString) => {
  if (!newDataString) return null
  
  try {
    const data = JSON.parse(newDataString)
    return data
  } catch (e) {
    return { rawData: newDataString }
  }
}

const getExerciseDisplayName = (exerciseId, exerciseName) => {
  if (exerciseName) return exerciseName
  if (!exerciseId) return null
  
  // Common exercise ID patterns - this can be enhanced based on actual system data
  const exercisePatterns = {
    'knee': 'Knee Exercise',
    'flex': 'Flexibility Exercise', 
    'range': 'Range of Motion',
    'stretch': 'Stretching Exercise',
    'strength': 'Strength Training',
    'walk': 'Walking Exercise',
    'balance': 'Balance Exercise',
    'mobility': 'Mobility Exercise'
  }
  
  const lowercaseId = exerciseId.toLowerCase()
  for (const [pattern, name] of Object.entries(exercisePatterns)) {
    if (lowercaseId.includes(pattern)) {
      return name
    }
  }
  
  // Return formatted ID if no pattern matches
  const shortId = exerciseId.length > 12 ? exerciseId.substring(0, 12) + '...' : exerciseId
  return `Exercise (${shortId})`
}

export const generateDescription = (activity) => {
  if (!activity) return ''
  
  const method = activity.method
  const collection = activity.collection_name
  const application = activity.application
  
  // Exercise events
  if (method === 'updateExercise' && collection === 'patientexerciseschedule') {
    return 'Patient completed an exercise'
  }
  if (method === 'editSchedule' && collection === 'patientexerciseschedule') {
    return 'Exercise schedule was updated by clinic staff'
  }
  
  // Questionnaire events
  if (method === 'updateFeedback' && collection === 'patientfeedschedule') {
    const newData = parseNewData(activity.newdata)
    let questionnaireName = 'a questionnaire'
    
    // The questionnaire name is in the 'shortname' field
    if (newData && newData.shortname) {
      questionnaireName = newData.shortname
    }

    // Entries logged before the server recorded `complete` have no flag and
    // keep the old wording.
    if (newData && newData.complete === false) {
      return `Patient saved ${questionnaireName} to finish later (${newData.answered} answered)`
    }

    return `Patient completed ${questionnaireName}`
  }
  if (method === 'editFeedSchedule' && collection === 'patientfeedschedule') {
    return 'Questionnaire schedule was updated by clinic staff'
  }
  
  // Login/Logout
  if (method === 'login' && application === 'PatientApp') {
    return 'Patient logged into the mobile/web application'
  }
  if (method === 'logout' && application === 'PatientApp') {
    return 'Patient logged out of the mobile/web application'
  }
  
  // Notifications
  if (method === 'notification' && collection === 'patientnotifications') {
    return 'System notification sent to patient'
  }
  
  // Patient notes
  if (collection === 'patientnotes') {
    const action = activity.action
    const newData = parseNewData(activity.newdata)
    const oldData = parseNewData(activity.olddata)
    
    
    // Handle different actions
    if (action === 'add') {
      if (!newData) {
        return 'Patient note added'
      }
      
      let descriptionParts = []
      if (newData.category) {
        descriptionParts.push(`Category: ${newData.category}`)
      }
      if (newData.time_logged && typeof newData.time_logged === 'number') {
        descriptionParts.push(`Time: ${formatTimeLogged(newData.time_logged)}`)
      }
      if (newData.notes) {
        const noteContent = newData.notes.trim()
        const truncatedNote = noteContent.length > 200 ? noteContent.substring(0, 200) + '...' : noteContent
        descriptionParts.push(`Note: ${truncatedNote}`)
      }
      
      return descriptionParts.join(' | ')
      
    } else if (action === 'update') {
      if (!newData && !oldData) {
        return 'Patient note updated'
      }
      
      let descriptionParts = []
      
      // Show what changed
      if (newData && oldData) {
        // Category change
        if (newData.category && oldData.category && newData.category !== oldData.category) {
          descriptionParts.push(`Category: ${oldData.category} → ${newData.category}`)
        } else if (newData.category) {
          descriptionParts.push(`Category: ${newData.category}`)
        }
        
        // Time logged change
        if (newData.time_logged !== oldData.time_logged) {
          const oldTime = oldData.time_logged ? formatTimeLogged(oldData.time_logged) : 'none'
          const newTime = newData.time_logged ? formatTimeLogged(newData.time_logged) : 'none'
          if (oldTime !== newTime) {
            descriptionParts.push(`Time: ${oldTime} → ${newTime}`)
          }
        } else if (newData.time_logged) {
          descriptionParts.push(`Time: ${formatTimeLogged(newData.time_logged)}`)
        }
        
        // Note content change
        if (newData.notes && oldData.notes && newData.notes.trim() !== oldData.notes.trim()) {
          const oldNote = oldData.notes.trim()
          const newNote = newData.notes.trim()
          const truncatedOld = oldNote.length > 100 ? oldNote.substring(0, 100) + '...' : oldNote
          const truncatedNew = newNote.length > 100 ? newNote.substring(0, 100) + '...' : newNote
          descriptionParts.push(`Note updated: "${truncatedOld}" → "${truncatedNew}"`)
        } else if (newData.notes) {
          const noteContent = newData.notes.trim()
          const truncatedNote = noteContent.length > 200 ? noteContent.substring(0, 200) + '...' : noteContent
          descriptionParts.push(`Note: ${truncatedNote}`)
        }
      } else if (newData) {
        // Only new data available
        if (newData.category) {
          descriptionParts.push(`Category: ${newData.category}`)
        }
        if (newData.time_logged) {
          descriptionParts.push(`Time: ${formatTimeLogged(newData.time_logged)}`)
        }
        if (newData.notes) {
          const noteContent = newData.notes.trim()
          const truncatedNote = noteContent.length > 200 ? noteContent.substring(0, 200) + '...' : noteContent
          descriptionParts.push(`Note: ${truncatedNote}`)
        }
      }
      
      return descriptionParts.length > 0 ? descriptionParts.join(' | ') : 'Patient note updated'
      
    } else if (action === 'delete') {
      if (!oldData) {
        return 'Patient note deleted (details not available)'
      }
      
      let descriptionParts = []
      if (oldData.category) {
        descriptionParts.push(`Category: ${oldData.category}`)
      }
      if (oldData.time_logged && typeof oldData.time_logged === 'number') {
        descriptionParts.push(`Time: ${formatTimeLogged(oldData.time_logged)}`)
      }
      if (oldData.notes) {
        const noteContent = oldData.notes.trim()
        const truncatedNote = noteContent.length > 200 ? noteContent.substring(0, 200) + '...' : noteContent
        descriptionParts.push(`Deleted note: ${truncatedNote}`)
      }
      
      return descriptionParts.join(' | ')
    }
    
    // Fallback for unknown actions
    return 'Patient note activity'
  }
  
  // Patient management
  if (collection === 'patients') {
    if (method === 'inactiveLog') {
      return 'System logged patient inactivity'
    }
    if (method === 'editPatient') {
      // olddata/newdata hold the full patient document; when the surgery
      // date differs between them, say so explicitly. Dates are stored at
      // noon UTC (calendar-day semantics), so compare and format in UTC.
      const oldData = parseNewData(activity.olddata)
      const newData = parseNewData(activity.newdata)
      const oldDate = oldData?.surgery?.date
      const newDate = newData?.surgery?.date
      if (
        oldDate && newDate &&
        !moment.utc(oldDate).isSame(moment.utc(newDate), 'day')
      ) {
        return `Surgery Date changed from ${moment.utc(oldDate).format('DD/MM/YYYY')} to ${moment.utc(newDate).format('DD/MM/YYYY')}`
      }
      return 'Patient information was updated by clinic staff'
    }
    if (method === 'creatPatientAdmin') {
      return 'New patient record was created'
    }
  }
  
  // Measurement events
  if (collection === 'devicemeasurement') {
    const newData = parseNewData(activity.newdata)
    let deviceName = 'device'
    
    // Extract device name from device_name field
    if (newData && newData.device_name) {
      deviceName = newData.device_name
    } else if (newData && newData.deviceName) {
      deviceName = newData.deviceName
    } else if (newData && newData.device && newData.device.name) {
      deviceName = newData.device.name
    } else if (newData && newData.device && newData.device.display_name) {
      deviceName = newData.device.display_name
    } else if (newData && newData.display_name) {
      deviceName = newData.display_name
    } else if (newData && newData.name) {
      deviceName = newData.name
    }
    
    return `Patient measurement taken ${deviceName}`
  }

  // TracPatch Progress Test (active ROM test)
  if (method === 'submitTracPatchData' || collection === 'tracpatchrom') {
    const newData = parseNewData(activity.newdata)
    if (newData && (newData.extension != null || newData.flexion != null)) {
      const ext = newData.extension != null ? Math.round(newData.extension) : '?'
      const flex = newData.flexion != null ? Math.round(newData.flexion) : '?'
      return `Patient completed the Progress Test: Extension ${ext}°, Flexion ${flex}°`
    }
    return 'Patient completed the Progress Test'
  }

  // Case management
  if (collection === 'actioncase') {
    return 'Support case activity'
  }
  
  // Device orders
  if (collection === 'deviceorders') {
    return 'Device order processed'
  }

  // Terms & Conditions acceptance
  if (collection === 'termsandconditions' && method === 'acceptTermsAndConditions') {
    const newData = parseNewData(activity.newdata)
    if (newData?.version) return `Version ${newData.version} Terms and Conditions form signed`
    return 'Terms and Conditions form signed'
  }

  // Consent form signing - handle both old format (array of titles) and new format (structured object)
  if (collection === 'patientconsents' && method === 'addConsentForRegisteredPatient') {
    const newData = parseNewData(activity.newdata)
    if (newData?.forms) return `Patient signed consent forms: ${newData.forms.map(f => f.title).join(', ')}`
    if (Array.isArray(newData)) return `Patient signed consent forms: ${newData.join(', ')}`
    return 'Patient signed consent forms'
  }

  // Wound images (upload only — deletes are intentionally not logged)
  if (collection === 'woundimages') {
    if (method === 'addWoundImage') return 'Patient sent a wound image'
    return 'Wound image activity'
  }

  // Generic fallback
  const displayMethod = method || 'Unknown action'
  const displayCollection = collection || 'unknown system'
  return `${displayMethod} performed on ${displayCollection}`
}

export const getUniqueEventNames = (activities) => {
  const eventNames = activities.map(activity => getEventName(activity))
  return [...new Set(eventNames)].sort()
}

export const getUniqueSources = (activities) => {
  const sources = activities.map(activity => getEventSource(activity))
  return [...new Set(sources)].sort()
}

export const filterActivities = (activities, filters) => {
  return activities.filter(activity => {
    if (!activity) return false

    // Notifications are hidden unless "Show Notifications Sent" is on —
    // except when the user explicitly picked Notification in the event
    // filter, which would otherwise always return an empty table.
    if (
      !filters.showNotifications &&
      getEventName(activity) === 'Notification' &&
      !(filters.eventNames && filters.eventNames.includes('Notification'))
    ) {
      return false
    }

    // Date filtering
    const activityDate = moment(activity.createdAt)
    
    if (filters.fromDate && activityDate.isBefore(moment(filters.fromDate))) {
      return false
    }
    
    if (filters.toDate && activityDate.isAfter(moment(filters.toDate).endOf('day'))) {
      return false
    }
    
    // Source filtering
    if (filters.sources && filters.sources.length > 0) {
      const activitySource = getEventSource(activity)
      if (!filters.sources.includes(activitySource)) {
        return false
      }
    }
    
    // Event name filtering
    if (filters.eventNames && filters.eventNames.length > 0) {
      const activityEventName = getEventName(activity)
      if (!filters.eventNames.includes(activityEventName)) {
        return false
      }
    }
    
    return true
  })
}

// Async function to enhance activities with exercise names
export const enhanceActivitiesWithNames = async (activities) => {
  const enhancedActivities = []
  
  for (const activity of activities) {
    const enhanced = { ...activity }
    
    // If it's an exercise, try to get the exercise name
    if (activity.method === 'updateExercise' && activity.collection_name === 'patientexerciseschedule') {
      try {
        const newData = parseNewData(activity.newdata)
        if (newData) {
          // First check if exercise_name is directly available in newdata (more efficient)
          if (newData.exercise_name) {
            enhanced.exerciseName = newData.exercise_name
          } 
          // Fall back to exerciseid lookup if exercise_name not available
          else if (newData.exerciseid) {
            const exercise = await serviceInterface.PatientService().getExercise(newData.exerciseid)
            if (exercise && exercise.name) {
              enhanced.exerciseName = exercise.name
            }
          }
        }
      } catch (error) {
        console.error('Error fetching exercise name:', error)
      }
    }
    
    enhancedActivities.push(enhanced)
  }
  
  return enhancedActivities
}

// Enhanced description generator that uses pre-fetched exercise names
export const generateEnhancedDescription = (activity) => {
  if (!activity) return ''
  
  const method = activity.method
  const collection = activity.collection_name
  const application = activity.application
  
  // Exercise events with enhanced names
  if (method === 'updateExercise' && collection === 'patientexerciseschedule') {
    if (activity.exerciseName) {
      return `Patient completed ${activity.exerciseName}`
    }
    return 'Patient completed an exercise'
  }
  
  // Patient notes with enhanced descriptions
  if (activity.collection_name === 'patientnotes') {
    return generateDescription(activity)
  }
  
  // Use the regular description generator for other events
  return generateDescription(activity)
}

export const exportToCSV = (activities, filename = null) => {
  const csvData = activities
    .filter(activity => activity && getEventName(activity) && getEventSource(activity))
    .map(activity => ({
      'Time': moment(activity.createdAt).format('MM/DD/YYYY, h:mm a'),
      'Source': getEventSource(activity),
      'Event Name': getEventName(activity),
      'Description': generateDescription(activity)
    }))
  
  if (csvData.length === 0) {
    return false
  }
  
  const headers = ['Time', 'Source', 'Event Name', 'Description']
  const csvContent = [
    headers.join(','),
    ...csvData.map(row => 
      headers.map(header => `"${(row[header] || '').toString().replace(/"/g, '""')}"`).join(',')
    )
  ].join('\n')
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  
  const defaultFilename = `activity-log-${moment().format('YYYY-MM-DD-HH-mm')}.csv`
  link.href = url
  link.download = filename || defaultFilename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
  
  return true
}

// Raw export function for Internal Export (SUPER_ADMIN only)
export const exportRawActivityLogs = (activities, filters, filename = null) => {
  console.log('exportRawActivityLogs - Input activities count:', activities.length)
  console.log('exportRawActivityLogs - Sample activity:', activities[0])
  
  // Apply date filtering to raw data
  let filteredActivities = activities
  
  if (filters.fromDate || filters.toDate) {
    filteredActivities = activities.filter(activity => {
      if (!activity) return false
      
      const activityDate = moment(activity.createdAt)
      
      if (filters.fromDate && activityDate.isBefore(moment(filters.fromDate))) {
        return false
      }
      
      if (filters.toDate && activityDate.isAfter(moment(filters.toDate).endOf('day'))) {
        return false
      }
      
      return true
    })
  }
  
  // Extract the specific fields for raw export
  const rawData = filteredActivities.map(activity => ({
    'createdAt': activity.createdAt ? moment(activity.createdAt).format('MM/DD/YYYY, h:mm a') : '',
    'collection_name': activity.collection_name || '',
    'action': activity.action || '',
    'method': activity.method || '',
    'application': activity.application || '',
    'actiontype': activity.actiontype || '',
    'newdata': activity.newdata || ''
  }))
  
  if (rawData.length === 0) {
    return false
  }
  
  const headers = ['createdAt', 'collection_name', 'action', 'method', 'application', 'actiontype', 'newdata']
  const csvContent = [
    headers.join(','),
    ...rawData.map(row => 
      headers.map(header => `"${(row[header] || '').toString().replace(/"/g, '""')}"`).join(',')
    )
  ].join('\n')
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  
  const defaultFilename = `internal-activity-export-${moment().format('YYYY-MM-DD-HH-mm')}.csv`
  link.href = url
  link.download = filename || defaultFilename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
  
  return true
}